import { useState, useCallback } from "react";
import { FormStep, ClientType } from "../types/form";
import {
  getMaxStepsForClient,
  getVisibleStepsForClient,
  isStepVisible,
} from "../utils/clientConfig";

const BASE_TOTAL_STEPS = 7;

export const useStepNavigation = (
  includePaymentStep: boolean = false,
  clientType: ClientType = "Default",
  isAuthenticated: boolean = false,
) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Get max steps based on client type
  const maxStepsForClient = getMaxStepsForClient(clientType);
  const adjustedBaseSteps = Math.min(BASE_TOTAL_STEPS, maxStepsForClient);

  const totalSteps = includePaymentStep
    ? adjustedBaseSteps + 2 
    : adjustedBaseSteps + 1; 

  const baseSteps: FormStep[] = [
    {
      id: 1,
      title: "Template Selection",
      description: "Choose your template",
      isActive: true,
      isCompleted: false,
    },
    {
      id: 2,
      title: "Customization & Branding",
      description: "Colors and branding",
      isActive: false,
      isCompleted: false,
    },
    {
      id: 3,
      title: "About Me",
      description: "Basic & contact details",
      isActive: false,
      isCompleted: false,
    },
    // Social Media is now integrated into About Me step
    {
      id: 4,
      title: "Customer Reviews",
      description: "Testimonials and reviews",
      isActive: false,
      isCompleted: false,
    },
  ];

  // Only include steps up to the client's max steps
  const clientSteps = baseSteps.slice(0, maxStepsForClient);

  const paymentStep: FormStep = {
    id: 1,
    title: "Payment",
    description: "Complete your payment",
    isActive: true,
    isCompleted: false,
  };

  const successStep: FormStep = {
    id: totalSteps, // Success step is always the last step
    title: "Success",
    description: "Profile created successfully",
    isActive: false,
    isCompleted: false,
  };

  // When payment step is included, Payment is step 1, Template Selection is step 2, and other steps are shifted by 1
  const stepsWithPayment = [
    paymentStep, // Payment (step 1)
    { ...clientSteps[0], id: 2 }, // Template Selection (step 2)
    ...clientSteps.slice(1).map((step, index) => ({
      ...step,
      id: step.id + 1,
    })),
    successStep,
  ];

  const stepsWithoutPayment = [...clientSteps, successStep];

  const steps = includePaymentStep ? stepsWithPayment : stepsWithoutPayment;

  const updateStepStatus = useCallback(() => {
    setCompletedSteps((prev) => {
      const newCompleted = new Set(prev);
      if (currentStep > 1) {
        newCompleted.add(currentStep - 1);
      }
      return newCompleted;
    });
  }, [currentStep]);

  const nextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      updateStepStatus();
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, totalSteps, updateStepStatus]);

  const prevStep = useCallback(() => {
    const successStepNumber = totalSteps; // Success step is always the last step
    
    // For authenticated users (editing), allow going back from success step
    if (isAuthenticated) {
      if (currentStep > 1) {
        setCurrentStep((prev) => prev - 1);
      }
    } else {
      // For non-authenticated users, cannot go back if on success step
      if (currentStep > 1 && currentStep !== successStepNumber) {
        setCurrentStep((prev) => prev - 1);
      }
    }
  }, [currentStep, totalSteps, isAuthenticated]);

  const goToStep = useCallback(
    (stepNumber: number) => {
      const successStepNumber = totalSteps; // Success step is always the last step
      
      // For authenticated users (editing), allow navigation from success step
      if (isAuthenticated) {
        if (stepNumber >= 1 && stepNumber <= totalSteps) {
          setCurrentStep(stepNumber);
        }
      } else {
        // For non-authenticated users, cannot navigate to any step if currently on success step
        if (currentStep === successStepNumber) {
          return;
        }
        
        if (stepNumber >= 1 && stepNumber <= totalSteps) {
          setCurrentStep(stepNumber);
        }
      }
    },
    [totalSteps, currentStep, isAuthenticated],
  );

  const canGoToStep = useCallback(
    (stepNumber: number) => {
      // Can go to current step or any completed step, but not to success step
      const successStepNumber = totalSteps; // Success step is always the last step
      
      // If user is on success step, they cannot navigate to any other step
      if (currentStep === successStepNumber) {
        return false;
      }
      
      if (stepNumber === successStepNumber) {
        return false; // Success step is not navigable
      }
      
      // For authenticated users (editing mode), allow navigation to all steps except success
      if (isAuthenticated && currentStep !== successStepNumber) {
        return stepNumber <= currentStep || completedSteps.has(stepNumber);
      }
      
      return stepNumber <= currentStep || completedSteps.has(stepNumber);
    },
    [currentStep, completedSteps, totalSteps, isAuthenticated],
  );

  const resetToFirstStep = useCallback(() => {
    setCurrentStep(1);
    setCompletedSteps(new Set());
  }, []);

  const getProgressPercentage = useCallback(() => {
    return (currentStep / totalSteps) * 100;
  }, [currentStep, totalSteps]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  const canGoNext = currentStep < totalSteps;
  
  // Calculate success step number
  const successStepNumber = totalSteps; // Success step is always the last step
  
  // Allow going back from any step except the first step and success step (for non-authenticated users)
  const canGoPrev = isAuthenticated 
    ? currentStep > 1 
    : currentStep > 1 && currentStep !== successStepNumber;

  // Function to get the visible step number for progress display
  const getVisibleStepNumber = useCallback(
    (currentStepNumber: number): number => {
      // Payment step is not visible in progress bar
      if (includePaymentStep && !isAuthenticated && currentStepNumber === 1) {
        return 0; // Payment step - not shown in progress
      }

      // Calculate visible step number (excluding payment step)
      if (includePaymentStep && !isAuthenticated) {
        return currentStepNumber - 1; // Shift down by 1 to exclude payment
      }

      return currentStepNumber;
    },
    [includePaymentStep, isAuthenticated],
  );

  // Function to get the total visible steps (excluding payment and success)
  const getTotalVisibleSteps = useCallback((): number => {
    // Return the number of visible form steps (excluding payment and success)
    return maxStepsForClient;
  }, [maxStepsForClient]);

  // Function to get the current step name based on client type and current step
  const getCurrentStepName = useCallback(
    (currentStepNumber: number): string => {
      if (includePaymentStep && currentStepNumber === 1) {
        return "Payment";
      }

      // Find the step in the current steps array
      const step = steps.find((s) => s.id === currentStepNumber);
      if (step && step.title) {
        return step.title;
      }

      // Fallback for success step
      if (currentStepNumber === totalSteps) {
        return "Success";
      }

      return "Unknown Step";
    },
    [includePaymentStep, steps, totalSteps],
  );

  // Function to get the current step description based on client type and current step
  const getCurrentStepDescription = useCallback(
    (currentStepNumber: number): string => {
      if (includePaymentStep && !isAuthenticated && currentStepNumber === 1) {
        return "Complete your payment to continue";
      }

      // Find the step in the current steps array
      const step = steps.find((s) => s.id === currentStepNumber);
      if (step && step.description) {
        return step.description;
      }

      // Fallback for success step
      if (currentStepNumber === totalSteps) {
        return "Profile created successfully";
      }

      return "Complete this step to continue";
    },
    [includePaymentStep, steps, totalSteps, isAuthenticated],
  );

  // Helper functions to determine current step type
  const isTemplateSelectionStep = useCallback(
    (stepNumber: number): boolean => {
      if (includePaymentStep && !isAuthenticated) {
        return stepNumber === 2; // Template Selection is step 2 when payment is step 1
      }
      return stepNumber === 1; // Template Selection is step 1 when no payment
    },
    [includePaymentStep, isAuthenticated],
  );

  const isPaymentStep = useCallback(
    (stepNumber: number): boolean => {
      return includePaymentStep && !isAuthenticated && stepNumber === 1;
    },
    [includePaymentStep, isAuthenticated],
  );

  // Basic and Contact merged into About Me

  // Social Media is now integrated into About Me step

  const isAboutMeStep = useCallback(
    (stepNumber: number): boolean => {
      if (includePaymentStep && !isAuthenticated) {
        return stepNumber === 4; // About Me is step 4 when payment is step 1
      }
      return stepNumber === 3; // About Me is step 3 when no payment
    },
    [includePaymentStep, isAuthenticated],
  );

  const isReviewsStep = useCallback(
    (stepNumber: number): boolean => {
      if (includePaymentStep && !isAuthenticated) {
        return stepNumber === 5; // Reviews is step 5 when payment is step 1
      }
      return stepNumber === 4; // Reviews is step 4 when no payment
    },
    [includePaymentStep, isAuthenticated],
  );

  const isCustomizationStep = useCallback(
    (stepNumber: number): boolean => {
      if (includePaymentStep && !isAuthenticated) {
        return stepNumber === 3; // Customization is step 3 when payment is step 1
      }
      return stepNumber === 2; // Customization is step 2 when no payment
    },
    [includePaymentStep, isAuthenticated],
  );

  const isSuccessStep = useCallback(
    (stepNumber: number): boolean => {
      // Success step is always the last step in the total steps
      return stepNumber === totalSteps;
    },
    [totalSteps],
  );

  // Function to get the step number for a specific step type
  const getStepNumberForType = useCallback(
    (stepType: string): number => {
      switch (stepType) {
        case "payment":
          return (includePaymentStep && !isAuthenticated) ? 1 : 0;
        case "templateSelection":
          return (includePaymentStep && !isAuthenticated) ? 2 : 1;
        case "customization":
          return (includePaymentStep && !isAuthenticated) ? 3 : 2;
        case "aboutMe":
          return (includePaymentStep && !isAuthenticated) ? 4 : 3;
        case "reviews":
          return (includePaymentStep && !isAuthenticated) ? 5 : 4;
        case "success":
          return totalSteps; // Success step is always the last step
        default:
          return 1;
      }
    },
    [includePaymentStep, adjustedBaseSteps, isAuthenticated], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Function to determine which step should be shown based on current step and client type
  const getCurrentStepType = useCallback(
    (currentStepNumber: number): string => {
      if (includePaymentStep && !isAuthenticated && currentStepNumber === 1) {
        return "payment";
      }

      if (includePaymentStep && !isAuthenticated && currentStepNumber === 2) {
        return "templateSelection";
      }

      // For authenticated users, step numbers are not shifted
      const actualStepNumber = (includePaymentStep && !isAuthenticated)
        ? currentStepNumber - 1
        : currentStepNumber;

      switch (actualStepNumber) {
        case 1:
          return "templateSelection";
        case 2:
          return "customization";
        case 3:
          return "aboutMe";
        case 4:
          return "reviews";
        default:
          return "templateSelection";
      }
    },
    [includePaymentStep, isAuthenticated],
  );

  // Function to check if a step should be rendered based on current step and client type
  const shouldShowStep = useCallback(
    (stepName: string, currentStepNumber: number): boolean => {
      // First check if the step is visible for this client type
      if (!isStepVisible(clientType, stepName)) {
        return false;
      }

      // Special handling for payment step
      if (stepName === "Payment" && includePaymentStep) {
        // Don't show payment step for authenticated users
        if (isAuthenticated) {
          return false;
        }
        return currentStepNumber === 1;
      }

      // Get the visible steps for this client type
      const visibleSteps = getVisibleStepsForClient(clientType);

      // Find the position of this step in the visible steps array
      const stepIndex = visibleSteps.indexOf(stepName);
      if (stepIndex === -1) {
        return false;
      }

      // Calculate the actual step number (accounting for payment step)
      let actualStepNumber;
      if (includePaymentStep && !isAuthenticated) {
        // If payment is included and user is not authenticated, step 1 is payment, step 2 is template selection
        if (stepIndex === 0) {
          actualStepNumber = 2; // Template Selection is step 2 when payment is first
        } else {
          actualStepNumber = stepIndex + 2; // Other steps are shifted by 2 (payment takes step 1)
        }
      } else {
        // If no payment or user is authenticated, form steps start at step 1
        actualStepNumber = stepIndex + 1;
      }

      // Check if this is the current step
      return currentStepNumber === actualStepNumber;
    },
    [clientType, includePaymentStep, isAuthenticated],
  );

  return {
    currentStep,
    totalSteps,
    steps,
    completedSteps,
    nextStep,
    prevStep,
    goToStep,
    canGoToStep,
    resetToFirstStep,
    getProgressPercentage,
    isFirstStep,
    isLastStep,
    canGoNext,
    canGoPrev,
    getVisibleStepNumber,
    getTotalVisibleSteps,
    getCurrentStepName,
    getCurrentStepDescription,
    isPaymentStep,
    isAboutMeStep,
    isReviewsStep,
    isCustomizationStep,
    isTemplateSelectionStep,
    isSuccessStep,
    getStepNumberForType,
    getCurrentStepType,
    shouldShowStep,
    isAuthenticated,
  };
};
